import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import BrandLogo from '../../../components/BrandLogo/BrandLogo';
import { NAVY } from '../websiteTokens';
import { useWebsiteTheme } from '../useWebsiteTheme';
import ThemeToggle from '../components/ThemeToggle';

const BRAND = 'EzeitPro';
const COMPANY = 'UtilStop LLC-FZ';
const EFFECTIVE = 'May 11, 2026';
const SECURITY_EMAIL = 'sales@ezeitpro.com';
const WEBSITE = 'www.ezeitpro.com';

const tocItems = [
  { id: 'overview', label: '1. Security Overview' },
  { id: 'encryption', label: '2. Data Encryption' },
  { id: 'access', label: '3. Access Controls' },
  { id: 'payment', label: '4. Payment Security (PCI-DSS)' },
  { id: 'infrastructure', label: '5. Infrastructure Security' },
  { id: 'incident', label: '6. Incident Response' },
  { id: 'employees', label: '7. Employee Access & Training' },
  { id: 'compliance', label: '8. Regulatory Compliance' },
  { id: 'your-role', label: '9. Your Security Responsibilities' },
  { id: 'sdlc', label: '10. Secure Development' },
  { id: 'report', label: '11. Report a Security Issue' },
];

export default function SecurityPage() {
  const navigate = useNavigate();
  const { isDark, toggleTheme } = useWebsiteTheme();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => { window.scrollTo(0, 0); }, []);


  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768);
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  const d = isDark ? {
    bg: NAVY.bg, surface: NAVY.surface, bgAlt: NAVY.bgAlt,
    text: NAVY.text, textSec: NAVY.textSec, textMuted: NAVY.textMuted,
    border: NAVY.border,
    primary: '#E91E63', accent: 'rgba(233,30,99,0.18)',
    badgeText: '#F48FB1',
    footBg: NAVY.footBg,
    greenBg: 'rgba(76,175,80,0.15)', greenText: '#81C784', greenBorder: 'rgba(76,175,80,0.3)',
    blueBg: 'rgba(21,101,192,0.18)', blueText: '#64B5F6', blueBorder: 'rgba(21,101,192,0.3)',
    sh: '0 1px 4px rgba(3,8,20,0.6)',
  } : {
    bg: '#FAFAFA', surface: '#FFFFFF', bgAlt: '#F5F5F5',
    text: '#212121', textSec: '#424242', textMuted: '#757575',
    border: 'rgba(0,0,0,0.08)',
    primary: '#C2185B', accent: '#FCE4EC',
    badgeText: '#C2185B',
    footBg: '#212121',
    greenBg: '#E8F5E9', greenText: '#2E7D32', greenBorder: '#C8E6C9',
    blueBg: '#E3F2FD', blueText: '#1565C0', blueBorder: '#90CAF9',
    sh: '0 1px 4px rgba(0,0,0,0.06)',
  };

  // Helper components — defined inside so they close over d and isMobile
  const S = ({ id, title, children }) => (
    <section id={id} style={{ marginBottom: '44px', scrollMarginTop: '80px' }}>
      <h2 style={{ fontSize: isMobile ? '17px' : '19px', fontWeight: 700, color: d.text, borderBottom: `2px solid ${d.accent}`, paddingBottom: '10px', marginBottom: '18px', margin: '0 0 18px' }}>{title}</h2>
      {children}
    </section>
  );

  const P = ({ children }) => (
    <p style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.85, marginBottom: '14px', marginTop: 0 }}>{children}</p>
  );

  const Ul = ({ items }) => (
    <ul style={{ margin: '0 0 14px 0', paddingLeft: '22px' }}>
      {items.map((t, i) => (
        <li key={i} style={{ fontSize: isMobile ? '14px' : '15px', color: d.textSec, lineHeight: 1.8, marginBottom: '6px' }}>{t}</li>
      ))}
    </ul>
  );

  const Sub = ({ title }) => (
    <h3 style={{ fontSize: isMobile ? '14px' : '15px', fontWeight: 700, color: d.text, marginBottom: '8px', marginTop: '18px' }}>{title}</h3>
  );

  const Badge = ({ label, bg, text }) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', background: bg || d.greenBg, color: text || d.greenText, fontSize: '12px', fontWeight: 700, padding: '4px 12px', borderRadius: '20px', marginRight: '8px', marginBottom: '6px' }}>
      <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: text || d.greenText, flexShrink: 0 }} />
      {label}
    </span>
  );

  const InfoBox = ({ title: boxTitle, items, bg, text, border }) => (
    <div style={{ background: bg || d.blueBg, border: `1px solid ${border || d.blueBorder}`, borderRadius: '10px', padding: isMobile ? '14px 16px' : '18px 20px', marginBottom: '16px' }}>
      <div style={{ fontSize: '12px', fontWeight: 700, color: text || d.blueText, marginBottom: '10px', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{boxTitle}</div>
      <ul style={{ margin: 0, paddingLeft: '18px' }}>
        {items.map((t, i) => (
          <li key={i} style={{ fontSize: isMobile ? '13px' : '14px', color: d.textSec, lineHeight: 1.75, marginBottom: '5px' }}>{t}</li>
        ))}
      </ul>
    </div>
  );

  const PhaseTable = ({ rows }) => (
    <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '10px', overflow: 'hidden', marginBottom: '16px' }}>
      {rows.map((row, i) => (
        <div key={i} style={{ display: isMobile ? 'flex' : 'grid', flexDirection: isMobile ? 'column' : undefined, gridTemplateColumns: isMobile ? undefined : '150px 1fr', borderBottom: i < rows.length - 1 ? `1px solid ${d.border}` : 'none' }}>
          <div style={{ padding: '12px 16px', background: d.bgAlt, fontWeight: 600, fontSize: '13px', color: d.primary, borderRight: isMobile ? 'none' : `1px solid ${d.border}`, borderBottom: isMobile ? `1px solid ${d.border}` : 'none' }}>{row.phase}</div>
          <div style={{ padding: '12px 16px', fontSize: '13px', color: d.textSec, lineHeight: 1.7 }}>{row.desc}</div>
        </div>
      ))}
    </div>
  );

  return (
    <div style={{ fontFamily: "'Roboto','Segoe UI',Arial,sans-serif", background: d.bg, color: d.text, minHeight: '100vh', transition: 'background 0.3s,color 0.3s', overflowX: 'hidden' }}>

      {/* Header */}
      <header style={{ background: d.surface, borderBottom: `1px solid ${d.border}`, padding: `0 ${isMobile ? '16px' : '32px'}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', height: '64px', position: 'sticky', top: 0, zIndex: 100, boxShadow: d.sh }}>
        <BrandLogo height={36} tone={isDark ? 'dark' : 'light'} />
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <ThemeToggle isDark={isDark} onToggle={toggleTheme} color={d.text} border={d.border} size={36} />
          <button
            onClick={() => navigate('/')}
            style={{ background: d.bgAlt, border: `1px solid ${d.border}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: d.textSec, fontWeight: 500, whiteSpace: 'nowrap' }}
          >
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Banner */}
      <div style={{ background: isDark ? 'linear-gradient(135deg,#1a0a2e 0%,#16213e 100%)' : `linear-gradient(135deg,${d.accent} 0%,#f5f5f5 100%)`, padding: isMobile ? '36px 20px 30px' : '48px 32px 40px', textAlign: 'center', borderBottom: `1px solid ${d.border}` }}>
        <div style={{ display: 'inline-block', background: d.primary, color: '#fff', fontSize: '11px', fontWeight: 700, padding: '4px 14px', borderRadius: '20px', marginBottom: '14px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Security & Data Protection</div>
        <h1 style={{ fontSize: isMobile ? '26px' : '36px', fontWeight: 700, color: d.text, margin: '0 0 10px' }}>Security Policy</h1>
        <p style={{ fontSize: '15px', color: d.textMuted, margin: '0 0 16px' }}>Effective Date: <strong style={{ color: d.textSec }}>{EFFECTIVE}</strong> &nbsp;·&nbsp; {COMPANY}</p>
        <div style={{ display: 'flex', justifyContent: 'center', flexWrap: 'wrap', gap: '6px' }}>
          <Badge label="256-bit Encryption" />
          <Badge label="PCI-DSS via Stripe" />
          <Badge label="GDPR Compliant" />
          <Badge label="UAE PDPL" bg={d.accent} text={d.badgeText} />
          <Badge label="India DPDPA" bg={d.accent} text={d.badgeText} />
        </div>
      </div>

      {/* Body grid */}
      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: isMobile ? '28px 16px 60px' : '48px 24px 80px', display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '220px 1fr', gap: isMobile ? '0' : '48px', alignItems: 'start' }}>

        {/* TOC — desktop sidebar */}
        {!isMobile && (
          <aside style={{ position: 'sticky', top: '80px' }}>
            <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '20px', boxShadow: d.sh }}>
              <div style={{ fontSize: '11px', fontWeight: 700, color: d.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '14px' }}>Contents</div>
              {tocItems.map(item => (
                <a key={item.id} href={`#${item.id}`}
                  style={{ display: 'block', fontSize: '13px', color: d.textSec, textDecoration: 'none', padding: '5px 0', borderBottom: `1px solid ${d.border}`, lineHeight: 1.4, transition: 'color 0.15s' }}
                  onMouseEnter={e => e.currentTarget.style.color = d.primary}
                  onMouseLeave={e => e.currentTarget.style.color = d.textSec}
                >
                  {item.label}
                </a>
              ))}
            </div>
          </aside>
        )}

        {/* TOC — mobile pill list */}
        {isMobile && (
          <div style={{ background: d.surface, border: `1px solid ${d.border}`, borderRadius: '12px', padding: '16px', marginBottom: '24px', boxShadow: d.sh }}>
            <div style={{ fontSize: '11px', fontWeight: 700, color: d.textMuted, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '10px' }}>Jump to section</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {tocItems.map(item => (
                <a key={item.id} href={`#${item.id}`}
                  style={{ fontSize: '12px', color: d.primary, background: d.accent, padding: '4px 10px', borderRadius: '20px', textDecoration: 'none', fontWeight: 500 }}
                >
                  {item.label.split('. ')[0]}
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Main content */}
        <main>

          <S id="overview" title="1. Security Overview">
            <P>{BRAND}, operated by {COMPANY}, is built on a security-first foundation. We understand that our customers trust us with sensitive business and personal data, and we take that responsibility seriously. This document describes the technical and organisational security measures we implement to protect your data.</P>
            <P>Our security programme is designed to meet or exceed the requirements of:</P>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px', marginBottom: '16px' }}>
              <Badge label="GDPR Article 32 (EEA)" />
              <Badge label="UAE PDPL" bg={d.accent} text={d.badgeText} />
              <Badge label="India DPDPA 2023" bg={d.accent} text={d.badgeText} />
              <Badge label="PCI-DSS via Stripe" />
            </div>
            <P>We continuously review and improve our security controls through internal audits, vulnerability assessments, and monitoring of the evolving threat landscape.</P>
          </S>

          <S id="encryption" title="2. Data Encryption">
            <Sub title="2.1 Data in Transit" />
            <P>All data transmitted between your browser or mobile app and our servers is encrypted using <strong>TLS 1.2 or higher</strong> (Transport Layer Security). We enforce HTTPS across all endpoints and implement HTTP Strict Transport Security (HSTS) with a minimum policy age of one year to prevent protocol downgrade attacks.</P>
            <Sub title="2.2 Data at Rest" />
            <P>All data stored in our databases and file storage systems is encrypted at rest using <strong>AES-256</strong> (Advanced Encryption Standard with 256-bit keys), an industry-standard encryption algorithm. Database backups are also encrypted using AES-256.</P>
            <Sub title="2.3 Key Management" />
            <P>Encryption keys are managed through AWS Key Management Service (KMS), with key rotation enforced on a scheduled basis. Keys are never stored alongside the data they protect. Access to key management operations is restricted to authorised infrastructure personnel only.</P>
            <InfoBox title="Encryption Standards Summary" items={[
              'In Transit: TLS 1.2+ (TLS 1.3 preferred), HSTS enforced',
              'At Rest: AES-256 for all database records and file storage',
              'Backups: AES-256 encrypted, geographically distributed',
              'Key Management: AWS KMS with automated rotation',
            ]} />
          </S>

          <S id="access" title="3. Access Controls">
            <Sub title="3.1 Role-Based Access Control (RBAC)" />
            <P>{BRAND} implements strict role-based access control. Users are assigned roles (Owner, Admin, Crew Member) with granular permissions limiting access to only the data and features required for their role. Vendor data is fully isolated — no vendor can access another vendor's data.</P>
            <Sub title="3.2 Authentication" />
            <Ul items={[
              'Passwords are hashed using bcrypt with a strong work factor; plaintext passwords are never stored',
              'Account lockout is enforced after multiple failed login attempts to prevent brute-force attacks',
              'Session tokens are issued with appropriate expiry periods and invalidated on logout',
              'Multi-factor authentication (MFA) is available and strongly recommended for all accounts',
            ]} />
            <Sub title="3.3 Principle of Least Privilege" />
            <P>All internal systems and services are configured to operate with the minimum permissions necessary to perform their function. Database access is segmented by service, and administrative access to production systems requires dedicated privileged access with full audit logging.</P>
          </S>

          <S id="payment" title="4. Payment Security (PCI-DSS)">
            <P>{BRAND} does not store, process, or transmit raw payment card data on our servers. All payment processing is handled exclusively by <strong>Stripe</strong>, a PCI-DSS Level 1 certified payment processor — the highest level of certification available in the payments industry.</P>
            <InfoBox
              title="How Payment Security Works"
              bg={d.greenBg} text={d.greenText} border={d.greenBorder}
              items={[
                "Your card details are entered directly into Stripe's secure, PCI-DSS-compliant payment fields",
                'A payment token is returned to our servers — this token cannot be used to recover card numbers',
                'We store only transaction references, amounts, and payment status — never card numbers, CVV codes, or expiry dates',
                "All Stripe communications are encrypted over TLS and authenticated using Stripe's API keys, stored securely as environment secrets",
              ]}
            />
            <P>For subscription billing, Stripe stores tokenised payment methods on your behalf. You may manage or remove stored payment methods at any time through your account settings.</P>
          </S>

          <S id="infrastructure" title="5. Infrastructure Security">
            <Sub title="5.1 Cloud Hosting" />
            <P>Our platform is hosted on Amazon Web Services (AWS), which maintains a comprehensive portfolio of security certifications including ISO 27001, SOC 2 Type II, and PCI-DSS. AWS infrastructure provides physical security, redundancy, and environmental controls at our data centres.</P>
            <Sub title="5.2 Network Security" />
            <Ul items={[
              'Virtual Private Cloud (VPC) with network segmentation isolates production, staging, and management environments',
              'Web Application Firewall (WAF) filters malicious traffic and protects against common web attacks (OWASP Top 10)',
              'DDoS protection through AWS Shield to mitigate volumetric and protocol attacks',
              'Security groups and network ACLs restrict inbound and outbound traffic to only required ports and protocols',
              'All administrative access to cloud infrastructure requires VPN and is restricted to authorised personnel',
            ]} />
            <Sub title="5.3 Availability and Backups" />
            <Ul items={[
              'Target uptime SLA: 99.9% (approximately 8.7 hours of downtime per year)',
              'Automated daily database backups retained for 30 days; weekly backups retained for 90 days',
              'Backups are stored in a geographically separate AWS region',
              'Disaster Recovery (DR) procedures are tested periodically to validate recovery time and recovery point objectives',
            ]} />
          </S>

          <S id="incident" title="6. Incident Response">
            <P>We maintain a documented Incident Response Plan (IRP) to ensure rapid and effective response to security incidents. Our process follows the Identify → Contain → Eradicate → Recover → Review lifecycle.</P>
            <PhaseTable rows={[
              { phase: 'Detect & Triage', desc: '24/7 monitoring and alerting detect anomalies; security team triages and classifies the severity within 1 hour of detection.' },
              { phase: 'Contain', desc: 'Immediate containment measures are applied to limit the scope of any breach, including network isolation and credential revocation.' },
              { phase: 'Eradicate & Recover', desc: 'Root cause is identified and removed; systems are restored from clean backups where necessary.' },
              { phase: 'Notify', desc: 'Affected customers are notified within 72 hours of confirmed breach, in compliance with GDPR Article 33, UAE PDPL, and India DPDPA notification requirements.' },
              { phase: 'Post-Incident Review', desc: 'A root cause analysis is conducted and preventive controls are updated within 14 days of resolution.' },
            ]} />
            <P>Data breach notifications to supervisory authorities are made within the legally required timeframes: 72 hours under GDPR, and as required under UAE PDPL and India DPDPA.</P>
          </S>

          <S id="employees" title="7. Employee Access & Training">
            <Ul items={[
              'Access to customer data is granted only to employees who require it to perform their job function (Principle of Least Privilege)',
              'All employees with access to production systems sign confidentiality and data protection agreements as part of their employment contract',
              'Background verification checks are conducted for all employees in roles with access to customer data',
              'All staff complete mandatory security awareness training covering phishing, social engineering, data handling, and incident reporting upon joining and annually thereafter',
              'Access rights are reviewed quarterly and revoked immediately upon employee departure',
              'All privileged access is logged, monitored, and subject to periodic access reviews',
            ]} />
          </S>

          <S id="compliance" title="8. Regulatory Compliance">
            <Sub title="8.1 General Data Protection Regulation (GDPR) — EEA / UK" />
            <P>For data subjects in the European Economic Area and United Kingdom, our security measures are designed to comply with GDPR Article 32, which requires appropriate technical and organisational measures to ensure a level of security appropriate to the risk, including:</P>
            <Ul items={[
              'Pseudonymisation and encryption of personal data (AES-256)',
              'Ability to ensure ongoing confidentiality, integrity, and availability of processing systems',
              'Ability to restore availability of personal data in a timely manner in the event of an incident',
              'Regular testing and evaluation of technical and organisational measures',
            ]} />
            <Sub title="8.2 UAE Personal Data Protection Law (UAE PDPL)" />
            <P>In accordance with UAE Federal Decree-Law No. 45 of 2021, we implement technical safeguards to prevent unauthorised access, disclosure, alteration, or destruction of personal data. We notify the UAE Data Office of any breach that is likely to prejudice the privacy of data subjects within the timeframes stipulated by the law.</P>
            <Sub title="8.3 India Digital Personal Data Protection Act 2023 (DPDPA)" />
            <P>For personal data of Indian data principals processed by {BRAND}, we implement security safeguards as required by the India DPDPA and its rules. We maintain records of processing activities and implement reasonable security safeguards to prevent personal data breaches. In the event of a breach, we notify the Data Protection Board of India and affected data principals as required by law.</P>
          </S>

          <S id="your-role" title="9. Your Security Responsibilities">
            <P>Security is a shared responsibility. While we implement comprehensive platform-level controls, you and your team play a vital role in maintaining security:</P>
            <Ul items={[
              `Use strong, unique passwords for your ${BRAND} account and do not reuse passwords from other services`,
              'Enable multi-factor authentication (MFA) on your account',
              'Limit account access to authorised team members only and remove access promptly when team members leave',
              'Do not share login credentials across team members; create individual accounts for each user',
              'Be vigilant about phishing emails — we will never ask for your password by email',
              `Keep the devices you use to access ${BRAND} updated with the latest security patches`,
              `Report any suspicious activity on your account to us immediately at ${SECURITY_EMAIL}`,
              'Ensure your customers are informed about how their data is used within your business, as you are an independent data controller for that data',
            ]} />
          </S>

          <S id="sdlc" title="10. Secure Development (SDLC)">
            <P>Security is embedded throughout our software development lifecycle:</P>
            <Ul items={[
              'Code reviews include security checks before any code is merged to production',
              'Automated static analysis (SAST) and dependency vulnerability scanning run on every pull request',
              'Third-party dependencies are reviewed and updated regularly; known vulnerable packages are patched within our SLA',
              'Penetration testing is conducted by independent security professionals on a periodic basis',
              'A staging environment mirrors production for testing; no real customer data is used in non-production environments',
              'Change management controls ensure all production deployments are reviewed and approved',
            ]} />
          </S>

          <S id="report" title="11. Report a Security Issue">
            <P>We take security reports seriously and appreciate responsible disclosure. If you discover a vulnerability in {BRAND}, please report it to us directly before disclosing it publicly. We commit to:</P>
            <Ul items={[
              'Acknowledge receipt of your report within 2 business days',
              'Provide an initial assessment within 5 business days',
              'Keep you informed of our progress as we work to resolve the issue',
              'Recognise your contribution (with your permission) once the issue is resolved',
              'Not take legal action against researchers who act in good faith and follow responsible disclosure principles',
            ]} />
            <div style={{ background: d.surface, border: `2px solid ${d.primary}`, borderRadius: '12px', padding: isMobile ? '20px 16px' : '28px 24px', textAlign: 'center' }}>
              <div style={{ fontSize: isMobile ? '13px' : '14px', color: d.textSec, marginBottom: '14px', lineHeight: 1.7 }}>
                To report a security vulnerability or concern, please email our security team:
              </div>
              <a
                href={`mailto:${SECURITY_EMAIL}?subject=Security Vulnerability Report — ${BRAND}`}
                style={{ display: 'inline-block', background: d.primary, color: '#fff', padding: '12px 28px', borderRadius: '28px', textDecoration: 'none', fontWeight: 600, fontSize: isMobile ? '14px' : '15px', wordBreak: 'break-all' }}
              >
                {SECURITY_EMAIL}
              </a>
              <div style={{ fontSize: '12px', color: d.textMuted, marginTop: '12px', lineHeight: 1.6 }}>
                Include: description, steps to reproduce, potential impact, and any screenshots or proof-of-concept (if safe to share)
              </div>
            </div>
          </S>

        </main>
      </div>

      {/* Footer */}
      <footer style={{ background: d.footBg, color: '#9E9E9E', padding: '24px 32px', textAlign: 'center', fontSize: '13px' }}>
        <p style={{ margin: 0 }}>
          © 2026 {COMPANY}, Dubai. All rights reserved. &nbsp;·&nbsp;
          <a href="/privacy-policy" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Privacy</a> &nbsp;·&nbsp;
          <a href="/terms" style={{ color: '#9E9E9E', textDecoration: 'none' }}>Terms</a> &nbsp;·&nbsp;
          <a href="/security" style={{ color: d.primary, textDecoration: 'none' }}>Security</a>
        </p>
      </footer>
    </div>
  );
}
